import * as askSuggestions from "@mirage/service-typeahead-search/service/sources/ask-suggestions";
import * as draftFromTemplate from "@mirage/service-typeahead-search/service/sources/draft-from-template";
import * as draftFromTone from "@mirage/service-typeahead-search/service/sources/draft-from-tone";
import * as draftWithSource from "@mirage/service-typeahead-search/service/sources/draft-with-source";
import * as mathCalculations from "@mirage/service-typeahead-search/service/sources/math-calculations";
import * as peopleFilters from "@mirage/service-typeahead-search/service/sources/people-filters";
import * as recentChats from "@mirage/service-typeahead-search/service/sources/recent-chats";
import * as recentQueries from "@mirage/service-typeahead-search/service/sources/recent-queries";
import * as recommendations from "@mirage/service-typeahead-search/service/sources/recommendations";
import * as searchFilters from "@mirage/service-typeahead-search/service/sources/search-filters";
import * as stackItems from "@mirage/service-typeahead-search/service/sources/stack-items";
import * as stacks from "@mirage/service-typeahead-search/service/sources/stacks";
import * as suggestedQueries from "@mirage/service-typeahead-search/service/sources/suggested-queries";
import * as urlShortcuts from "@mirage/service-typeahead-search/service/sources/url-shortcuts";
import {
  SourceId,
  typeahead,
} from "@mirage/service-typeahead-search/service/types";
import {
  and,
  containsIncompleteTrailingFilterBinding,
  doesNotContainFilterBindingPrefix,
  doesNotContainIncompleteTrailingFilterBinding,
  doesNotIntroduceMultiSelectFor,
  doesntHaveRecentQueries,
  exactQueryLength,
  hasRecentQueries,
  isModeIn,
  isNotURLShortcut,
  isOmniboxDisabled,
  isOmniboxEnabled,
  isPeopleFilterRowEnabled,
  isRecentQueriesDisabled,
  isRecentQueriesEnabled,
  isTypeaheadFiltersEnabled,
  isURLShortcut,
  minimumQueryLength,
  minStrippedQueryLength,
  nullStateRecommendationsDisabled,
  nullStateRecommendationsEnabled,
  or,
} from "@mirage/service-typeahead-search/service/utils/predicate-factory";
import { SearchFilterType } from "@mirage/shared/search/search-filters";

// NOTE: order the sources by execution priority
export const TYPEAHEAD_SEARCH_SOURCES: typeahead.PredicateSubject<typeahead.SourceId>[] =
  [
    /**
     * Sources that affect "null state", i.e. empty query
     */

    {
      id: SourceId.RecentQueries,
      search: recentQueries.search,
      predicate: or(
        and(
          isOmniboxEnabled(),
          isModeIn(typeahead.Mode.Default, typeahead.Mode.Find),
          isNotURLShortcut(),
          doesNotContainFilterBindingPrefix(),
        ),
        and(
          isOmniboxDisabled(),
          isRecentQueriesEnabled(),
          or(
            and(exactQueryLength(0), nullStateRecommendationsDisabled()),
            // Null state: If user has recent queries, show those
            // Omnibox Note: I don't think we need to check hasRecentQueries() in our new conditions
            and(
              exactQueryLength(0),
              nullStateRecommendationsEnabled(),
              hasRecentQueries(),
            ),
            and(minimumQueryLength(1)),
          ),
          isNotURLShortcut(),
          doesNotContainFilterBindingPrefix(),
        ),
      ),
    },
    {
      id: SourceId.Recommendations,
      search: recommendations.search,
      predicate: or(
        and(
          isOmniboxEnabled(),
          isModeIn(typeahead.Mode.Default, typeahead.Mode.Find),
          isNotURLShortcut(),
          doesNotContainFilterBindingPrefix(),
        ),
        and(
          isOmniboxDisabled(),
          or(
            // Null state: If user doesn't have recent queries, show recommendations
            and(
              exactQueryLength(0),
              nullStateRecommendationsEnabled(),
              or(isRecentQueriesDisabled(), doesntHaveRecentQueries()),
            ),
            and(minimumQueryLength(1)),
          ),
          isNotURLShortcut(),
          doesNotContainFilterBindingPrefix(),
        ),
      ),
    },

    {
      id: SourceId.PeopleFilters,
      search: peopleFilters.raw,
      predicate: and(
        isOmniboxEnabled(),
        isPeopleFilterRowEnabled(),
        isModeIn(typeahead.Mode.Default, typeahead.Mode.Find),
        isNotURLShortcut(),
        doesNotContainFilterBindingPrefix(),
        minimumQueryLength(1),
      ),
    },

    /**
     * Sources that don't affect "null state", i.e. empty query
     */

    {
      id: SourceId.SuggestedQueries,
      search: suggestedQueries.search,
      predicate: or(
        and(
          isOmniboxEnabled(),
          or(
            and(
              isModeIn(typeahead.Mode.Default, typeahead.Mode.Find),
              minimumQueryLength(1),
              doesNotContainIncompleteTrailingFilterBinding(true),
            ),
            and(
              isModeIn(typeahead.Mode.Ask, typeahead.Mode.Write),
              minimumQueryLength(1),
            ),
            isModeIn(typeahead.Mode.Organize),
          ),
          isNotURLShortcut(),
        ),
        and(
          isOmniboxDisabled(),
          minimumQueryLength(1),
          isNotURLShortcut(),
          or(doesNotContainFilterBindingPrefix(), minStrippedQueryLength(1)),
          doesNotContainIncompleteTrailingFilterBinding(false),
        ),
      ),
    },
    {
      id: SourceId.URLShortcuts,
      search: urlShortcuts.search,
      // do not restrict shortcuts to only dropboxers as there are external shortcuts now!
      // the filtering of dropboxer-only shortcuts is done in the typeahead search function
      predicate: or(
        and(
          isOmniboxEnabled(),
          isModeIn(typeahead.Mode.Default, typeahead.Mode.Find),
          minimumQueryLength(1),
          isURLShortcut(),
          doesNotContainFilterBindingPrefix(),
        ),
        and(
          isOmniboxDisabled(),
          minimumQueryLength(1),
          isURLShortcut(),
          doesNotContainFilterBindingPrefix(),
        ),
      ),
    },
    {
      id: SourceId.Stacks,
      search: stacks.search,
      predicate: or(
        and(
          isOmniboxEnabled(),
          isModeIn(typeahead.Mode.Default, typeahead.Mode.Find),
          minimumQueryLength(1),
          isNotURLShortcut(),
          doesNotContainFilterBindingPrefix(),
        ),
        and(
          isOmniboxDisabled(),
          minimumQueryLength(1),
          isNotURLShortcut(),
          doesNotContainFilterBindingPrefix(),
        ),
      ),
    },
    {
      id: SourceId.StackItems,
      search: stackItems.search,
      predicate: or(
        and(
          isOmniboxEnabled(),
          isModeIn(typeahead.Mode.Default, typeahead.Mode.Find),
          minimumQueryLength(1),
          isNotURLShortcut(),
          doesNotContainFilterBindingPrefix(),
        ),
        and(
          isOmniboxDisabled(),
          minimumQueryLength(1),
          isNotURLShortcut(),
          doesNotContainFilterBindingPrefix(),
        ),
      ),
    },
    {
      id: SourceId.MathCalculations,
      search: mathCalculations.search,
      predicate: or(
        and(
          isOmniboxEnabled(),
          isModeIn(typeahead.Mode.Default, typeahead.Mode.Find),
          minimumQueryLength(1),
          isNotURLShortcut(),
          doesNotContainFilterBindingPrefix(),
        ),
        and(
          isOmniboxDisabled(),
          minimumQueryLength(1),
          isNotURLShortcut(),
          doesNotContainFilterBindingPrefix(),
        ),
      ),
    },
    {
      id: SourceId.SearchFilter,
      search: searchFilters.search,
      predicate: or(
        and(
          isOmniboxEnabled(),
          isModeIn(typeahead.Mode.Default, typeahead.Mode.Find),
          minimumQueryLength(1),
          isNotURLShortcut(),
          isTypeaheadFiltersEnabled(),
          containsIncompleteTrailingFilterBinding(),
          doesNotIntroduceMultiSelectFor(SearchFilterType.Person),
          doesNotIntroduceMultiSelectFor(SearchFilterType.ContentType),
          doesNotIntroduceMultiSelectFor(SearchFilterType.LastUpdated),
        ),
        and(
          isOmniboxDisabled(),
          minimumQueryLength(1),
          isNotURLShortcut(),
          isTypeaheadFiltersEnabled(),
          containsIncompleteTrailingFilterBinding(),
          doesNotIntroduceMultiSelectFor(SearchFilterType.Person),
          doesNotIntroduceMultiSelectFor(SearchFilterType.ContentType),
          doesNotIntroduceMultiSelectFor(SearchFilterType.LastUpdated),
        ),
      ),
    },
    {
      id: SourceId.AskSuggestions,
      search: askSuggestions.search,
      predicate: and(
        isOmniboxEnabled(),
        isModeIn(typeahead.Mode.Default, typeahead.Mode.Ask),
        isNotURLShortcut(),
        doesNotContainFilterBindingPrefix(),
      ),
    },
    {
      id: SourceId.RecentChats,
      search: recentChats.search,
      predicate: and(
        isOmniboxEnabled(),
        isModeIn(typeahead.Mode.Default, typeahead.Mode.Ask),
        isNotURLShortcut(),
        doesNotContainFilterBindingPrefix(),
      ),
    },
    {
      id: SourceId.DraftWithSource,
      search: draftWithSource.search,
      predicate: and(
        isOmniboxEnabled(),
        or(
          isModeIn(typeahead.Mode.Write),
          and(isModeIn(typeahead.Mode.Default), exactQueryLength(0)),
        ),
        isNotURLShortcut(),
        doesNotContainFilterBindingPrefix(),
      ),
    },
    {
      id: SourceId.DraftFromTemplate,
      search: draftFromTemplate.search,
      predicate: and(
        isOmniboxEnabled(),
        or(
          isModeIn(typeahead.Mode.Write),
          and(isModeIn(typeahead.Mode.Default), exactQueryLength(0)),
        ),
        isNotURLShortcut(),
        doesNotContainFilterBindingPrefix(),
      ),
    },
    {
      id: SourceId.DraftFromTone,
      search: draftFromTone.search,
      predicate: () => false,
    },
  ];
